/**
 * Brandlive Notes
 * This file was manually copied from source https://www.gstatic.com/meetjs/index.d.ts
 * TODO - Once this is hosted as a package, remove file and install
 */

declare global {
	export interface Window {
		commonFrames: {
			dialog: (element: Element) => FrameDescriptor<FrameOptions>;
			docked: (element: Element) => FrameDescriptor<FrameOptions>;
		};
		meet: () => MeetAppBuilder;
	}
}

// Original file: frames.types.d.ts

/**
 * A full `FrameSet` with full customization available. Specify the elements
 * the `MeetApp` should use for various components.
 *
 * It's recommended that you use the utils on `window.commonFrames` to create
 * these, as they'll set the default descriptor options.
 */
export interface FrameSetDescriptor {
	dialog: FrameDescriptor<FrameOptions>;
	docked: FrameDescriptor<FrameOptions>;
	pip: FrameDescriptor<PipFrameOptions>;
}

/**
 * Options for basic frames.
 */
export interface FrameOptions {
	cssClasses: string;
}

/**
 * Options for the picture-in-picture (PIP) frame.
 */
export interface PipFrameOptions extends FrameOptions { }

/**
 * Element + options pair that describes a given frame.
 */
export interface FrameDescriptor<OptionsType> {
	element: Element;
	options?: OptionsType;
}


// Original file: app.types.d.ts


/**
 * A mutable `MeetApp` config.
 */
export interface MeetAppBuilder {
	/**
	 * Create a `MeetApp` from the current MeetAppBuilder.
	 */
	build(args: MeetAppRequiredConfiguration): MeetApp;
	// TODO(b/205166049): Make this work for multiple handlers
	/**
	 * Attach an event handler to a `MeetAppEvent`. You can only attach one
	 * handler to each event type. Repeated handlers overwrite the existing
	 * handler.
	 */
	on<T extends keyof MeetAppCallbacks>(
		eventId: T, eventHandler: MeetAppCallbacks[T]): this;
	/**
	 * Supplies a custom frame set for the `MeetApp`. If this isn't called,
	 * a default set is supplied based on your eventual `parentElement`.
	 */
	useFrameSet(frameSet: FrameSetDescriptor): MeetAppBuilder;
	/**
	 * Set the `authUser` field that's passed to the `MeetApp`.
	 */
	setAuthUser(authUser: string): this;
	/**
	 * Set if Meet should ignore browser compatibility.
	 */
	setIgnoreBrowserCompatibilityChecks(ignoreBrowserCompatibilityChecks:
		boolean): this;
	/**
	 * Set the host product name for UI customizations in Meet.
	 */
	setHostProductName(hostProductName: string): this;
	/**
	 * Set if Meet should share the host's `window.location.href` with other
	 * participants in the call.
	 */
	setHostUrlSharing(shareHostUrl: boolean, hostUrl?: string): this;
	/**
	 * Set the retry options passed to the `MeetApp`.
	 */
	useRetryOptions(retryOptions: MeetAppRetryOptions): this;
	/**
	 * Set the `captureHandle` from a client. For more information, see {@link
	 * https://developer.chrome.com/docs/web-platform/capture-handle/
	 * | Capture Handle}.
	 */
	setCaptureHandle(captureHandle: string): this;
	/**
	 * Set the supported presentation type for the app.
	 */
	setPresentationSupport(presentationSupport: PresentationSupport): this;
	/**
	 * Set whether the call can be popped out to Google Meet in an independent
	 * browser tab.
	 */
	setPopOutSupport(supportsPopOut: boolean): this;
}

/**
 * Required configuration parameters for a `MeetApp`, supplied at start time.
 * Separate from the optional parameters supplied using the `MeetAppBuilder`.
 */
export interface MeetAppRequiredConfiguration {
	/**
	 * OAuth access token
	 */
	accessToken: string;
	apiKey: string;
	parentElement: Element;
}

/**
 * A running Meet app that's attached to some iframes after `init()`. The
 * configuration is immutable. Imperative commands are issued through method
 * calls.
 */
export interface MeetApp {
	/**
	 * Initialize the `MeetApp`. If this method isn't called directly, it's
	 * called internally when another method is called that requires the
	 * `MeetApp` to be initialized.
	 */
	init(): Promise<void>;
	/**
	 * Transfer an active Meet call to this instance of the `MeetApp`.
	 */
	transferCall(transferToken: string): Promise<void>;
	/**
	 * Set the HTML element to be presented. See {@link
	 * https://developer.chrome.com/docs/web-platform/region-capture/
	 * | Region Capture}.
	 */
	cropPresentationsTo(element: HTMLDivElement): Promise<void>;
	/**
	 * Join a Meet call by url.
	 */
	joinCallFromUrl(url: string): Promise<StartCallResult>;
}

/**
 * Information required to attach a document to the meeting this `MeetApp` is
 * running.
 */
export interface DocumentInfo {
	url: string;
	name?: string;
}

/**
 * All the `HostCallbacks` that the `MeetApp` can attach to.
 */
export interface MeetAppCallbacks {
	readonly showToast?: (toast: { message: string }) => void;
	readonly linkClicked?: (link: { url: string }) => void;
}

/**
 * Retry options, error handling, and success notifications.
 */
export interface MeetAppRetryOptions {
	pipRetryIntervalsMs?: number[];
	onBeforePipRetry?: () => void;
	onPipSuccess?: () => void;
	onPipTimeout?: () => void;
	onDialogRequest?: () => void;
	onDialogSuccess?: () => void;
	onDialogTimeout?: () => void;
}

/** Possible results of `startCall`. */
// tslint:disable-next-line:ban-const-enum
export const enum StartCallResult {
	/**
	 * Unknown call result.
	 */
	UNKNOWN_START_CALL_RESULT,
	/**
	 * The requested call is starting.
	 */
	STARTING,
	/**
	 * The start call request was ignored.
	 */
	IGNORED,
	/**
	 * The start call request was ignored because a previous request is still in
	 * progress.
	 */
	IGNORED_START_IN_PROGRESS,
}

/**
 * The types of content the user can present to a call.
 */
// A constant for third-party and internal use or visibility.
// tslint:disable-next-line:ban-const-enum
export const enum PresentationSupport {
	/**
	 * The user presentation support is unknown.
	 */
	UNKNOWN,
	/**
	 * The user cannot present content to the call.
	 */
	NONE,
	/**
	 * The user can present this tab to the call.
	 */
	THIS_TAB,
}